import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '../services/api';

export const useApplicationsStore = defineStore('applications', () => {
  // State
  const applications = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Секреты в списке не приходят — запрашиваются по одному и живут только в памяти вкладки
  const secrets = new Map();

  // Getters
  const applicationsList = computed(() => applications.value);
  const isLoading = computed(() => loading.value);
  const errorMessage = computed(() => error.value);

  // Actions
  const setLoading = (value) => {
    loading.value = value;
  };

  const setError = (errorMessage) => {
    error.value = errorMessage;
  };

  const setApplications = (apps) => {
    applications.value = apps;
  };

  // Секрет из ответа API кладём в кэш, а в реактивный список — приложение без него
  const withoutSecret = ({ secret, ...app }) => {
    if (secret) {
      secrets.set(app.id, secret);
    }
    return app;
  };

  const addApplication = (app) => {
    applications.value.push(withoutSecret(app));
  };

  const updateApplication = (updatedApp) => {
    const index = applications.value.findIndex((app) => app.id === updatedApp.id);
    if (index !== -1) {
      applications.value.splice(index, 1, withoutSecret(updatedApp));
    }
  };

  const removeApplication = (appId) => {
    applications.value = applications.value.filter((app) => app.id !== appId);
    secrets.delete(appId);
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/applications');
      // Ключи могли перевыпустить в другой вкладке — кэш секретов сбрасываем вместе со списком
      secrets.clear();
      setApplications((response.data.data || []).map(withoutSecret));
    } catch (error) {
      setError(error.response?.data?.message || 'Ошибка при загрузке приложений');
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const createApplication = async (appData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/applications', appData);
      addApplication(response.data.data);
      return response.data.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Ошибка при создании приложения');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationById = async ({ id, ...appData }) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.put(`/applications/${id}`, appData);
      updateApplication(response.data.data);
      return response.data.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Ошибка при обновлении приложения');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteApplication = async (appId) => {
    try {
      setLoading(true);
      setError(null);

      await api.delete(`/applications/${appId}`);
      removeApplication(appId);
    } catch (error) {
      setError(error.response?.data?.message || 'Ошибка при удалении приложения');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchSecret = async (appId) => {
    if (!secrets.has(appId)) {
      const response = await api.get(`/applications/${appId}`);
      secrets.set(appId, response.data.data.secret);
    }
    return secrets.get(appId);
  };

  // Перевыпуск key и secret: старые перестают работать сразу
  const regenerateCredentials = async (appId) => {
    const response = await api.post(`/applications/${appId}/regenerate-credentials`);
    const { key, secret } = response.data.data;

    secrets.set(appId, secret);
    const app = applications.value.find((item) => item.id === appId);
    if (app) {
      updateApplication({ ...app, key });
    }
    return { key, secret };
  };

  return {
    // State
    applications,
    loading,
    error,
    // Getters
    applicationsList,
    isLoading,
    errorMessage,
    // Actions
    fetchApplications,
    createApplication,
    updateApplicationById,
    deleteApplication,
    fetchSecret,
    regenerateCredentials,
  };
});
